<template>
  <div class="settings-css manage">
    <div
      v-if="!checkPermission('manage_settings') || forbidden" >
      <h1 class="settings__title">
        Consent management
      </h1>
      <Forbidden />
    </div>
    <div
      v-else-if="!forbidden && business && business.name"
      class="settings--limit-width">
      <h1 class="settings__title">
        Consent management
      </h1>

      <Spinner
        v-if="reloadingAutomationSettings"
        class="mt-5"
        :thickness="2" />

      <div
        v-else
        class="settings--box big" >
        <ValidationObserver
          ref="customFieldObserver"
          v-slot="{ valid, dirty, pristine }" >
          <b-form-group>
            <label class="flex flex-align-center">
              Opt-out keyword
              <SBIcon
                id="stop-word-description"
                class="ml-1"
                :icon="SBIconList.INFORMATION"
                :color="SBColors.greyPrimary"
                :size="12" />
            </label>
            <div id="stop-input">
              <p /> <!-- This p tag is just a placeholder for the popover to be triggered. -->
              <b-input
                value="Stop"
                class="mb-2"
                disabled/>
            </div>
            
            <b-popover
              target="stop-word-description"
              triggers="hover"
              placement="auto">
              Contacts can opt-out from receiving messages by sending you STOP keyword.
            </b-popover>
            <b-popover
              target="stop-input"
              triggers="hover"
              placement="bottom"
              custom-class="basic-popover">
              This can't be edited.
            </b-popover>
          </b-form-group>

          <b-form-group>
            <label class="flex flex-align-center">
              Opt-out automated message
              <SBIcon
                id="unsubscribe-message-description"
                class="ml-1"
                :icon="SBIconList.INFORMATION"
                :color="SBColors.greyPrimary"
                :size="12" />
              <b-popover
                target="unsubscribe-message-description"
                triggers="hover"
                placement="auto">
                A message that will be sent to contact when they unsubscribe.
              </b-popover>
            </label>
            <ValidationProvider
              v-slot="{ errors }"
              rules="required"
              name="Message" >
              <!-- show-tags-select - enable once tags are refactored as part of broadcast feature -->
              <SBTextInput
                ref="messageArea"
                v-model="unsubscribeComputedModel"
                :focus-shadow="true"
                placeholder="Unsubscribe message..."
                :business-id="business.id" />
              <div
                v-if="errors[0]"
                class="error flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.ERROR"
                  :color="SBColors.redPrimary"
                  :size="12" />
                <span>{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </b-form-group>

          <FooterActions
            :form-valid="valid"
            :has-changes="dirty"
            :show-cancel="!pristine"
            :save-busy="saving"
            @reset="resetChanges"
            @save="updateNotificationSettings" />
        </ValidationObserver>
      </div>

    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import ToasterMixin from '@/mixins/Toaster';
import Spinner from '@/components/common/Spinner';
import SBTextInput from '@/components/business/widgets/SBTextInput';
import { whatsappFormatMessage } from '@/helpers/stringHelpers';
import FooterActions from './partials/FooterActions';
import NoPermissionMixin from '@/mixins/AccessPermission';
import UserPermissionsMixin from '@/mixins/UserPermissions.mixin';

export default {
  name: 'ConsentSettings',
  components: {
    Spinner,
    SBTextInput,
    FooterActions,
  },
  mixins: [
    ToasterMixin,
    NoPermissionMixin,
    UserPermissionsMixin,
  ],
  data() {
    return {
      // unsubscribeText: null,
      initialChangedUnsubscribedText: null,
      newUnsubscribeText: null,
      saving: false,
      reloadingAutomationSettings: true,
    };
  },
  computed: {
    ...mapGetters({
      automationSettings: 'automation/automationSettings',
      unsubscribeMessageSaved: 'automation/unsubscribeMessageSaved',
      apiError: 'automation/apiError',
      userPermissions: 'user/userPermissions',
    }),
    business() {
      return this.currentOrganisation.businesses.find(b => b.id === this.$route.params.businessId);
    },
    unsubscribeComputedModel: {
      get() {
        let returnValue;
        if (this.newUnsubscribeText) {
          returnValue = this.newUnsubscribeText;
        }
        return returnValue;
      },
      set(changedContent) {
        changedContent = whatsappFormatMessage(changedContent);

        // On first dynamic edit of the field with the data retrived from BE
        if (this.initialChangedUnsubscribedText === null) {
          this.$nextTick(() => { this.$refs.customFieldObserver.reset(); });
          this.initialChangedUnsubscribedText = changedContent;
        }
        this.newUnsubscribeText = changedContent;

        // Reset the validator if message edit was undone
        if (this.initialChangedUnsubscribedText === this.newUnsubscribeText) {
          this.$nextTick(() => { this.$refs.customFieldObserver.reset(); });
        }
      },
    },
  },
  watch: {
    automationSettings(settings) {
      if (settings) {
        this.reloadingAutomationSettings = false;
        // this.$refs.messageArea.resetTextTo(quillFormatMessage(settings.unsubscribe_message, { parseTags: false }));
        // this.unsubscribeText = settings.unsubscribe_message;
        // this.$refs.messageArea.setText(this.automationSettings.unsubscribe_message);
        // this.unsubscribeComputedModel = settings.unsubscribe_message;
        this.initialChangedUnsubscribedText = null;
        // this.newUnsubscribeText = null;
        this.$nextTick(() => {
          this.$refs.messageArea.setText(this.automationSettings.unsubscribe_message);
        });
      }
    },
    unsubscribeMessageSaved(saved) {
      if (saved) {
        this.saving = false;
        this.showToaster('Opt-out message has been successfully updated.', null, null, 'OK');
        this.initialChangedUnsubscribedText = this.newUnsubscribeText;
        this.$refs.customFieldObserver.reset();
      }
    },
    userPermissions() {
      this.businessChangedCallback();
    },
    apiError(error) {
      this.saving = false;
      if (error.status === 422) {
        this.showToaster(error.data.errors, null, null, 'error');
      } else {
        this.showToaster(error.message || 'Something went wrong. Please try again.', null, null, 'error');
      }
    },
  },
  mounted() {
    this.finalizeSplashView();
    if (this.business.id) {
      this.businessChangedCallback();
    }
  },
  methods: {
    ...mapActions({
      fetchSettings: 'automation/fetchSettings',
      updateUnsubscribeMessage: 'automation/updateUnsubscribeMessage',
      finalizeSplashView: 'organisation/finalizeSplashView',
    }),
    businessChangedCallback() {
      if (this.checkPermission('manage_settings') && this.business.id) {
        this.reloadingAutomationSettings = true;
        this.fetchSettings(this.business.id);
      }
    },
    resetChanges() {
      this.$refs.messageArea.setText(this.automationSettings.unsubscribe_message);
    },
    updateNotificationSettings() {
      this.saving = true;
      const payload = {
        merchant_id: this.business.id,
        data: {
          unsubscribe_message: this.unsubscribeComputedModel,
        },
      };
      this.updateUnsubscribeMessage(payload);
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .text-input-component .text-input-wrapper {
    max-height: unset;

    .input-scroll-area {
      height: unset;
      min-height: 72px;
      max-height: calc(100vh - 400px);
    }
}
</style>
